<template>
    <Head title="Role Play Summary" />
    <div class="relative grow flex flex-col overflow-clip max-h-full background">
        <div class="grow overflow-y-auto overflow-x-hidden min-h-0 pb-6 px-6 lg:px-16">
            <div class="mx-auto max-w-[1000px]">
                <OverallSummary
                    :overall-summary="overallSummary"
                    :summary-state="summaryState"
                    :strengths="strengths"
                    :weaknesses="weaknesses"
                    :skills-breakdown="skillsBreakdown"
                    :role-play-messages="role_play_messages"
                    :show-practice-again="!!practice_again_params"
                    :skipped="skipped"
                    @resume-conversation="handleResumeConversation"
                    @practice-again="handlePracticeAgain" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };

// FIXME: this is defined here as an object and in RolePlaySummary/constants.ts as a TS enum
// this is used in not yet TS converted vue files
export const STATE = {
    LOADING: "loading",
    LOADED: "loaded",
};
</script>

<script setup lang="ts">
import { getCookie } from "/js/utils";
import { Head, router } from "@inertiajs/vue3";
import type { GlobalProperties } from "~vue/appTypes";
import { createRealtimeClient } from "~vue/RealtimeClientFactory.js";
import { SummaryState } from "~vue/RolePlaySummary/constants";
import OverallSummary from "~vue/RolePlaySummary/OverallSummary.vue";
import {
    type RolePlayData,
    type SkillPerformanceResponse,
    type SocketData,
    type SummaryCompleteData,
    SummaryDataType,
    type SummaryMessage,
    SummaryMessageType,
    type SummaryResponse,
} from "~vue/RolePlaySummary/types";
import type { RealtimeClient } from "~vue/StreamingPostClient";
import { createWebSocketErrorHandler } from "~vue/utils/logUtils";
import isEmpty from "lodash/isEmpty";
import { inject, onBeforeMount, onMounted, onUnmounted, ref } from "vue";

type PracticeAgainParams = {
    name: string;
    relationship: string;
    personality: string;
    context: string;
};

type RolePlaySummaryProps = {
    summary: SummaryResponse | null;
    skills: Array<SkillPerformanceResponse>;
    role_play: {
        // FIXME: this is an incomplete type, defining only what is used for now
        hashid: string;
    };
    // FIXME: this is an incomplete type, generic object for now, should be properly typed later
    role_play_messages: Array<object>;
    stream_url: string;
    cs_id: string;
    cs_uuid?: string | null;
    return_url: string;
    practice_again_params?: PracticeAgainParams | null;
};

const { summary, skills, role_play, role_play_messages, stream_url, cs_id, cs_uuid = null, return_url, practice_again_params = null } = defineProps<RolePlaySummaryProps>();

const skipped = ref(false);

const skillsBreakdown = ref(
    // loads 4 instances for loading state
    Array(4).fill({
        state: {
            name: SummaryState.LOADING,
            rationale: SummaryState.LOADING,
            how_to_improve: SummaryState.LOADING,
        },
    }),
);
const strengths = ref<string[]>([]);
const weaknesses = ref<string[]>([]);
const overallSummary = ref<string>("");
const summaryState = ref({ strengths: SummaryState.LOADING, weaknesses: SummaryState.LOADING, overallSummary: SummaryState.LOADING });

const { $setEventContext, $sendSocketEvent } = inject("globalProperties", {} as GlobalProperties);
const socketClient = ref<RealtimeClient | null>(null);

const checkAndUpdateState = () => {
    if (strengths.value.length > 0 && summaryState.value.strengths === SummaryState.LOADING) {
        summaryState.value.strengths = SummaryState.LOADED;
    }
    if (weaknesses.value.length > 0 && summaryState.value.weaknesses === SummaryState.LOADING) {
        summaryState.value.weaknesses = SummaryState.LOADED;
    }
    if (overallSummary.value && summaryState.value.overallSummary === SummaryState.LOADING) {
        summaryState.value.overallSummary = SummaryState.LOADED;
    }
};

const updateSummary = (rolePlayData: RolePlayData) => {
    strengths.value = rolePlayData.strengths;
    weaknesses.value = rolePlayData.weaknesses;
    overallSummary.value = rolePlayData.overallSummary ?? "";
    checkAndUpdateState();
};

const updateSkillBreakdown = (skills: SkillPerformanceResponse[]) => {
    skills.forEach((skill, index) => {
        if (index - 1 > 0) {
            // if its at a new index, set the previous index to loaded
            skillsBreakdown.value[index - 1] = {
                ...skillsBreakdown.value[index - 1],
                state: {
                    name: SummaryState.LOADED,
                    rationale: SummaryState.LOADED,
                    how_to_improve: SummaryState.LOADED,
                },
            };
        }
        skillsBreakdown.value[index] = {
            ...skillsBreakdown.value[index],
            state: {
                name: skill.name ? SummaryState.LOADED : SummaryState.LOADING,
                rationale: skill.rationale ? SummaryState.LOADED : SummaryState.LOADING,
                how_to_improve: skill.how_to_improve ? SummaryState.LOADED : SummaryState.LOADING,
            },
            name: skill.name,
            score: skill.score,
            rationale: skill.rationale,
            how_to_improve: skill.how_to_improve,
        };
    });
};

const markSummaryAsLoaded = () => {
    summaryState.value.strengths = SummaryState.LOADED;
    summaryState.value.weaknesses = SummaryState.LOADED;
    summaryState.value.overallSummary = SummaryState.LOADED;
};

// used to set all of skill breakdown to loaded state, needed when no data is returned for a given skill section
const markSkillBreakdownAsLoaded = () => {
    skillsBreakdown.value.forEach((skill) => {
        skill.state.name = SummaryState.LOADED;
        skill.state.rationale = SummaryState.LOADED;
        skill.state.how_to_improve = SummaryState.LOADED;
    });
};

onBeforeMount(() => {
    if (!isEmpty(skills) && !isEmpty(summary)) {
        // if data is available via inertia prop, it exists in DB, set the values and set state to loaded
        updateSummary({
            overallSummary: summary.overall_summary,
            strengths: summary.strengths ?? [],
            weaknesses: summary.weaknesses ?? [],
        });
        markSummaryAsLoaded();

        updateSkillBreakdown(skills);
        markSkillBreakdownAsLoaded();
    }
});

onMounted(() => {
    $setEventContext({ window_id: cs_id });

    if (!isEmpty(skills) && !isEmpty(summary)) {
        // short circuit and don't connect to a websocket if the data is already available
        return;
    }

    socketClient.value = createRealtimeClient(stream_url, {
        reconnectEnabled: true,
        reconnectInterval: 2000,
    });
    socketClient.value.connect();
    socketClient.value.onMessage = (message: { data: string | SummaryMessage }) => {
        const messageData = typeof message.data === "string" ? (JSON.parse(message.data) as SummaryMessage) : message.data;
        if (messageData.type === SummaryMessageType.PARTIAL) {
            handleRolePlayPartialAddition(messageData.data);
        } else if (messageData.type === SummaryMessageType.COMPLETE) {
            handleRolePlaySummaryComplete(messageData.data);
        }
    };

    socketClient.value.onOpen = () => {
        if (socketClient.value != null) {
            $sendSocketEvent("summarize_role_play", { role_play_hashid: role_play.hashid }, socketClient.value);
        }
    };
    socketClient.value.onError = createWebSocketErrorHandler({ cs_id, cs_uuid }, "RolePlaySummary");
});

const closeSocket = () => {
    if (socketClient.value) {
        socketClient.value.removeListeners();
        socketClient.value.close();
    }
};

onUnmounted(() => {
    $setEventContext({});
    closeSocket();
});

const updateRolePlayData = (wsData: SocketData) => {
    if (wsData.type === SummaryDataType.SUMMARY) {
        updateSummary({ overallSummary: wsData.data.overall_summary ?? "", strengths: wsData.data.strengths ?? [], weaknesses: wsData.data.weaknesses ?? [] });
    } else if (wsData.type === SummaryDataType.BREAKDOWN) {
        if (wsData.data.skill_scoring) {
            updateSkillBreakdown(wsData.data.skill_scoring);
        }
    }
};

const handleRolePlayPartialAddition = (wsData: SocketData) => {
    updateRolePlayData(wsData);
};

const handleRolePlaySummaryComplete = (wsData: SummaryCompleteData) => {
    if (wsData.skipped) {
        skipped.value = true;
        markSummaryAsLoaded();
        markSkillBreakdownAsLoaded();
        closeSocket();
        return;
    }
    if (wsData.final_summary) {
        updateSummary({
            overallSummary: wsData.final_summary.overall_summary ?? "",
            strengths: wsData.final_summary.strengths ?? [],
            weaknesses: wsData.final_summary.weaknesses ?? [],
        });
    }
    if (wsData.final_breakdown?.skill_scoring) {
        updateSkillBreakdown(wsData.final_breakdown.skill_scoring);
    }
    markSummaryAsLoaded();
    markSkillBreakdownAsLoaded();
    closeSocket();
};

const handleResumeConversation = () => {
    router.visit(return_url);
};

const handlePracticeAgain = () => {
    if (!practice_again_params) {
        return;
    }
    router.post("/coach/realtime_chat/", practice_again_params, {
        headers: { "X-CSRFToken": getCookie("csrftoken") ?? "" },
    });
};
</script>

<style scoped>
.background {
    background: radial-gradient(40% 15% at 15% 105%, rgba(255, 197, 61, 0.5) 0%, rgba(255, 197, 61, 0) 100%),
        radial-gradient(40% 15% at 50% 105%, rgba(254, 40, 144, 0.24) 0%, rgba(254, 40, 144, 0) 100%),
        radial-gradient(40% 15% at 85% 105%, rgba(169, 32, 203, 0.24) 0%, rgba(169, 32, 203, 0) 100%);
}
</style>
